// La demo del Asistente arriba de la caja de Crear (ADR-2026-10-09-asistente-en-crear).
//
// Como el onboarding: Cumbre habla en globos. Muestra, sin que nadie toque nada, lo que se
// puede pedir — el usuario pide un carrusel con la foto de su producto y Cumbre lo arma en
// cuatro placas —. Es SÓLO una demostración: la caja de abajo queda vacía para el pedido de
// verdad. Cuando el usuario empieza a usarla, la demo se achica a una pastilla
// (`minimizada`) para dejarle el protagonismo; no desaparece.
//
// Corre UNA vez: termina con todo el diálogo escrito y el carrusel armado, y queda así
// (ADR-2026-10-09-asistente-en-crear-2). Los globos de los dos lados se escriben letra por
// letra, con un cursor que titila mientras tanto.
//
// El guion (qué se ve en cada momento) es `guionDelAsistente`; acá se cuenta el tiempo y se
// dibuja. Minimizada no corre: no tiene sentido animar lo que no se ve.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight, Check } from 'lucide-react';
import { CumbreMascot } from '../../../../components/CumbreMascot';
import {
  DEMO_TERMINADA,
  FIN_DE_LA_DEMO_MS,
  LISTO_DE_LA_DEMO,
  PEDIDO_DE_LA_DEMO,
  RESPUESTA_DE_LA_DEMO,
  SALUDO_DE_LA_DEMO,
  TOTAL_DE_PLACAS,
  estadoDeLaDemo,
  letrasEscritas,
  type EstadoDeLaDemo,
} from './guionDelAsistente';
import './crear.css';
import { DEMO_POR_RUBRO, type RubroDeCrear } from './contenidoPorRubro';

/** La foto del producto que "adjunta" el usuario y las placas del carrusel que arma Cumbre. */
// La foto del producto y las cuatro placas salen del rubro de la marca
// (`DEMO_POR_RUBRO`, ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro).
const DEGRADE_DE_MARCA = 'linear-gradient(181.87deg, var(--brand-primary-claro) 8.76%, var(--brand-primary-oscuro) 90.56%)';

/** Cuenta el tiempo de a 50 ms mientras `corre`, hasta el final de la demo: ahí se queda. */
function useRelojDeLaDemo(corre: boolean): EstadoDeLaDemo {
  const [quieta] = useState(() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  const [ms, setMs] = useState(0);
  const termino = ms >= FIN_DE_LA_DEMO_MS;
  useEffect(() => {
    if (!corre || quieta || termino) return;
    const id = window.setInterval(() => setMs((t) => Math.min(FIN_DE_LA_DEMO_MS, t + 50)), 50);
    return () => window.clearInterval(id);
  }, [corre, quieta, termino]);
  return quieta ? DEMO_TERMINADA : estadoDeLaDemo(ms);
}

/**
 * Un texto que se escribe letra por letra. Está ENTERO desde el principio (lo que falta,
 * transparente): así el globo nace con su tamaño final y las palabras no saltan de renglón.
 * Mientras escribe, un cursor titila al final de lo escrito (sin ancho propio: no empuja).
 */
const Escrito: React.FC<{ tramos: readonly { texto: string; clase?: string }[]; escrito: number }> = ({ tramos, escrito }) => {
  const total = tramos.reduce((n, t) => n + t.texto.length, 0);
  const escritas = letrasEscritas(total, escrito);
  const escribiendo = escrito > 0 && escrito < 1;
  let desde = 0;
  return (
    <>
      {tramos.map((t, i) => {
        const visibles = Math.max(0, Math.min(t.texto.length, escritas - desde));
        // El cursor va en el tramo donde está la próxima letra.
        const aca = escribiendo && escritas >= desde && escritas < desde + t.texto.length;
        desde += t.texto.length;
        return (
          <span key={i} className={t.clase}>
            {t.texto.slice(0, visibles)}
            {aca && <span aria-hidden="true" className="-mr-[2px] inline-block h-[1em] w-[2px] translate-y-[2px] animate-pulse rounded-full bg-current align-baseline" />}
            <span className="text-transparent">{t.texto.slice(visibles)}</span>
          </span>
        );
      })}
    </>
  );
};

/**
 * La pila de globos, pegada abajo (contra la caja). Cuando se suma un globo, la pila crece
 * con una transición de altura: como está anclada abajo, su borde de arriba SUBE despacio y
 * se lleva a los globos de antes, en vez de que salten. Recorta mientras crece (el globo
 * nuevo aparece desde abajo); el margen negativo con padding deja lugar a las sombras.
 */
const PilaQueSube: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const adentroRef = useRef<HTMLDivElement | null>(null);
  const [alto, setAlto] = useState<number | null>(null);
  useLayoutEffect(() => {
    const nodo = adentroRef.current;
    if (!nodo) return;
    setAlto(nodo.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setAlto(e?.borderBoxSize?.[0]?.blockSize ?? nodo.offsetHeight));
    ro.observe(nodo);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      className="-mx-6 -mb-6 -mt-6 overflow-hidden px-6 pb-6 pt-6 transition-[height] duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none"
      style={alto === null ? undefined : { height: alto + 48, boxSizing: 'border-box' }}
    >
      <div ref={adentroRef} className="flex flex-col gap-2.5 md:gap-3">
        {children}
      </div>
    </div>
  );
};

const Globo: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`crear-aparece rounded-[18px] rounded-bl-[6px] bg-white shadow-[0_10px_24px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] ${className}`}>{children}</div>
);

export const DemoDelAsistente: React.FC<{
  /** El usuario empezó a usar la caja: la demo se achica a su pastilla. */
  minimizada: boolean;
  /** Ya mandó un pedido: la pastilla dice dónde sigue la conversación. */
  conversando?: boolean;
  /** De qué rubro son el producto y las placas. */
  rubro?: RubroDeCrear;
}> = ({ minimizada, conversando = false, rubro = 'moda' }) => {
  const e = useRelojDeLaDemo(!minimizada);
  const { producto: FOTO_DEL_PRODUCTO, placas: PLACAS } = DEMO_POR_RUBRO[rubro];
  // Cumbre "habla" (ojos grandes) mientras se escribe uno de sus globos.
  const hablando =
    (e.saludo && e.escritoSaludo < 1) || (e.respuesta && e.escritoRespuesta < 1) || (e.listo && e.escritoListo < 1);
  const estadoDeCumbre = e.pensando ? 'thinking' : hablando ? 'answering' : 'idle';

  return (
    <section
      aria-label="Así funciona el Asistente"
      className={`relative mx-auto w-full max-w-[760px] transition-[height] duration-500 ease-out ${
        minimizada ? 'h-[84px] md:h-[100px]' : 'h-[268px] md:h-[322px]'
      }`}
    >
      {/* ── La conversación de ejemplo ── */}
      <div className="crear-demo absolute inset-0 flex flex-col justify-end" data-minimizada={minimizada} aria-hidden={minimizada || undefined}>
        {/* Como un chat: la conversación arranca pegada a la caja y cada globo nuevo empuja a
            los anteriores hacia arriba (ADR-2026-10-09-asistente-en-crear-3). */}
        <PilaQueSube>
          {/* Cumbre saluda y pregunta. */}
          <div className="flex items-start gap-2.5 md:gap-3">
            <CumbreMascot state={estadoDeCumbre} size={56} decorativa className="cumbre--mini h-auto w-[38px] shrink-0 md:w-[56px]" />
            {e.saludo && (
              <Globo className="mt-0.5 px-3.5 py-2 text-[13.5px] font-semibold text-brand-black md:mt-1 md:px-4 md:py-2.5 md:text-[15px]">
                <Escrito
                  tramos={[{ texto: SALUDO_DE_LA_DEMO[0] }, { texto: SALUDO_DE_LA_DEMO[1], clase: 'text-brand-primary' }]}
                  escrito={e.escritoSaludo}
                />
              </Globo>
            )}
          </div>

          {/* El pedido del usuario, escribiéndose. */}
          {e.pedido && (
            <div className="flex justify-end">
              <div
                className="crear-aparece flex max-w-[92%] items-center gap-2 rounded-[18px] rounded-br-[6px] py-1.5 pl-1.5 pr-3.5 text-[12.5px] font-semibold text-white shadow-[0_12px_24px_-12px_rgb(var(--brand-primary-rgb)_/_0.7)] md:gap-2.5 md:pr-4 md:text-[14.5px]"
                style={{ background: DEGRADE_DE_MARCA }}
              >
                <img src={FOTO_DEL_PRODUCTO} alt="" className="h-9 w-7 shrink-0 rounded-md object-cover ring-2 ring-white/80 md:h-10 md:w-8" />
                <span>
                  <Escrito tramos={[{ texto: PEDIDO_DE_LA_DEMO }]} escrito={e.escritoPedido} />
                </span>
              </div>
            </div>
          )}

          {/* Cumbre piensa y después contesta con el carrusel. */}
          <div className="ml-[48px] empty:hidden md:ml-[68px]">
            {e.pensando && (
              <Globo className="inline-flex gap-1.5 px-4 py-3.5">
                <span className="crear-punto" />
                <span className="crear-punto" />
                <span className="crear-punto" />
              </Globo>
            )}
            {e.respuesta && (
              <Globo className="w-fit max-w-full p-2.5 md:p-3.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12.5px] font-semibold text-brand-black md:text-[14px]">
                    <Escrito tramos={[{ texto: RESPUESTA_DE_LA_DEMO }]} escrito={e.escritoRespuesta} />
                  </span>
                  {e.listo ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#1F8F6A] md:hidden">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                      Listo
                    </span>
                  ) : (
                    <span className="relative block h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.095)] md:w-28">
                      <span
                        className="absolute inset-y-0 left-0 rounded-full bg-[linear-gradient(90deg,var(--brand-primary-claro),var(--brand-accent))] transition-[width] duration-100 ease-linear"
                        style={{ width: `${Math.round(e.armado * 100)}%` }}
                      />
                    </span>
                  )}
                </div>
                <div className="mt-2 flex gap-1.5 md:mt-2.5 md:gap-2">
                  {PLACAS.slice(0, TOTAL_DE_PLACAS).map((p, i) =>
                    i < e.placas ? (
                      <div
                        key={i}
                        className="crear-placa relative h-[60px] w-[48px] shrink-0 overflow-hidden rounded-lg shadow-[0_10px_18px_-10px_rgba(11,26,74,0.55)] md:h-[108px] md:w-[86px] md:rounded-[11px]"
                      >
                        <img src={p.foto} alt="" className="h-full w-full object-cover" />
                        <span className="absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(11,16,51,0.7))]" />
                        <span className="absolute inset-x-1 bottom-1 flex items-end justify-between gap-1 text-[7px] font-extrabold leading-tight text-white md:inset-x-2 md:bottom-2 md:text-[11px]">
                          {p.texto}
                          {p.llamada && (
                            <span className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full bg-white text-brand-primary md:h-5 md:w-5">
                              <ArrowRight className="h-2 w-2 md:h-3 md:w-3" strokeWidth={3} aria-hidden="true" />
                            </span>
                          )}
                        </span>
                      </div>
                    ) : (
                      // El lugar de la placa que todavía no salió: se ve que se está armando.
                      <div key={i} className="h-[60px] w-[48px] shrink-0 animate-pulse rounded-lg bg-[rgb(var(--brand-primary-rgb)_/_0.07)] md:h-[108px] md:w-[86px] md:rounded-[11px]" />
                    ),
                  )}
                </div>
                {e.listo && (
                  <div className="crear-aparece mt-2.5 hidden items-center gap-2 text-[12.5px] font-bold text-[#1F8F6A] md:flex">
                    <span className="grid h-[18px] w-[18px] place-items-center rounded-full bg-[#DDF7EC]">
                      <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
                    </span>
                    <span>
                      <Escrito tramos={[{ texto: LISTO_DE_LA_DEMO }]} escrito={e.escritoListo} />
                    </span>
                  </div>
                )}
              </Globo>
            )}
          </div>
        </PilaQueSube>
      </div>

      {/* ── La demo achicada: una pastilla que dice dónde escribirle ──
          Antes recordaba qué arma Cumbre ("carruseles, posts y videos", con las placas), y la
          indicación de escribir iba suelta debajo. Achicada no tenía sentido repetir la demo: la
          pastilla dice lo que hay que hacer ahora
          (ADR-2026-10-20-la-pastilla-del-asistente-dice-donde-escribir). */}
      <div className="crear-demo-mini absolute inset-x-0 top-0 flex flex-col items-center" data-visible={minimizada} aria-hidden={!minimizada || undefined}>
        <div className="flex max-w-full items-center gap-2.5 rounded-full bg-white/90 py-1.5 pl-1.5 pr-4 shadow-[0_12px_28px_-16px_rgb(var(--brand-primary-rgb)_/_0.5)] ring-1 ring-[rgb(var(--brand-primary-rgb)_/_0.1)] md:gap-3 md:py-2 md:pl-2 md:pr-5">
          <CumbreMascot size={36} decorativa className="cumbre--mini h-auto w-[28px] shrink-0 md:w-[36px]" />
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[12.5px] font-bold text-brand-black md:text-[14px]">
            <span className="truncate">{conversando ? 'El Asistente te responde en el chat' : 'Escribí abajo y el Asistente te responde'}</span>
            {!conversando && <ArrowDown className="h-3.5 w-3.5 shrink-0 text-brand-primary md:h-4 md:w-4" strokeWidth={2.25} aria-hidden="true" />}
          </span>
        </div>
      </div>
    </section>
  );
};

export default DemoDelAsistente;
